/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import { CampaignType, type CampaignData } from '../../types';

/**
 * Normalise a URL for advertorial-match lookup so that publisher-injected
 * tracking params (utm_*, click IDs, fragments) and trailing-slash differences
 * don't break attribution. Returns undefined for unparsable input.
 *
 * Both the configured `eurl` keys and discovered anchor `href`s pass through
 * this so that lookup is symmetric.
 */
export function normaliseAdvertorialMatchUrl(rawUrl: string): string | undefined {
  try {
    const parsed = new URL(rawUrl, document.baseURI);
    let pathname = parsed.pathname;
    if (pathname.length > 1 && pathname.endsWith('/')) {
      pathname = pathname.slice(0, -1);
    }
    return parsed.origin + pathname;
  } catch {
    return undefined;
  }
}

/**
 * Build a URL -> Ad delivery ID map from `eurl` configured against ads on
 * Monitoring campaigns. URL match is the attribution mechanism for passive
 * monitoring of publisher-served advertorials; other campaign types continue
 * to attribute via `eid` (creative ID) / `elid` (line ID) match.
 *
 * Keys are normalised so that `eurl` configured with or without trailing
 * slashes / query strings / fragments matches anchors regardless of how the
 * publisher served the URL.
 *
 * Last-write-wins on duplicate URLs across campaigns - misconfiguration that
 * the management API should prevent at edit time.
 */
export function buildAdvertorialMatchUrlMap(campaignData: CampaignData[]): Map<string, string> {
  const urlMap = new Map<string, string>();
  for (const campaign of campaignData) {
    if (campaign.type !== CampaignType.MONITORING) {
      continue;
    }
    for (const ad of campaign.ads) {
      if (!ad.eurl) {
        continue;
      }
      const normalised = normaliseAdvertorialMatchUrl(ad.eurl);
      if (normalised) {
        urlMap.set(normalised, ad.id);
      }
    }
  }
  return urlMap;
}
